export const ROUTES = {
	home: "/",
	dashboard: "/",
	portfolio: "/portfolio",
	motokoMaker: "/motokomaker",
	motokoDollar: "/motokodollar",
	borrow: "/borrow",
	vaults: "/vaults",
	vaultDetail: (id: string | number | bigint) => `/vaults/${id}`,
	treasury: "/treasury",
	liquidity: "/liquidity",
	mmkBook: "/mmk-book",
	pegswap: "/pegswap",
	liquidation: "/liquidation",
	governance: "/governance",
	wallet: "/wallet",
	savings: "/savings",
	profile: "/profile",
	analytics: "/analytics",
	oracle: "/oracle",
	cycles: "/cycles",
	docs: "/docs",
	exploreUsdm: "/explore/usdm",
	exploreMmk: "/explore/mmk",
	institutions: "/institutions",
	agents: "/agents",
	skills: "/skills",
	intelligence: "/intelligence",
	more: "/more",
} as const;

export type PrimaryTab = {
	to: string;
	label: string;
	cta?: boolean;
	shortLabel?: string;
};

export type NavSection = {
	to: string;
	label: string;
	desc: string;
	/** lucide icon key used by More / sheets */
	icon:
		| "borrow"
		| "vaults"
		| "liquidate"
		| "analytics"
		| "treasury"
		| "governance"
		| "wallet"
		| "savings"
		| "pegswap"
		| "liquidity"
		| "oracle"
		| "cycles"
		| "profile"
		| "docs"
		| "agents"
		| "skills"
		| "intelligence"
		| "explore"
		| "institutions"
		| "more";
};

/** Desktop top nav — product sections (Wallet lives in top bar + More only) */
export const PRIMARY_TABS: readonly PrimaryTab[] = [
	{ to: ROUTES.borrow, label: "Borrow" },
	{ to: ROUTES.vaults, label: "Vaults" },
	{ to: ROUTES.liquidation, label: "Liquidate" },
	{ to: ROUTES.analytics, label: "Analytics" },
	{ to: ROUTES.treasury, label: "Treasury" },
	{ to: ROUTES.governance, label: "Governance" },
	{ to: ROUTES.pegswap, label: "PegSwap" },
	{ to: ROUTES.mmkBook, label: "MMK Book" },
	{ to: ROUTES.liquidity, label: "Liquidity" },
] as const;

/**
 * Full product catalog for More page / sheet — ordered, with descriptions.
 * Agent Layer destinations included for discoverability (also via footer CTA).
 */
export const ALL_SECTIONS: readonly NavSection[] = [
	{
		to: ROUTES.borrow,
		label: "Borrow",
		desc: "Deposit collateral and mint USDM against ICP, ckBTC, or ckETH.",
		icon: "borrow",
	},
	{
		to: ROUTES.vaults,
		label: "Vaults",
		desc: "Manage positions — deposit, mint, repay, and withdraw free collateral.",
		icon: "vaults",
	},
	{
		to: ROUTES.liquidation,
		label: "Liquidate",
		desc: "Fixed-spread liquidations and Dutch auctions for at-risk vaults.",
		icon: "liquidate",
	},
	{
		to: ROUTES.analytics,
		label: "Analytics",
		desc: "Certified TVL, debt, vault counts, and protocol health.",
		icon: "analytics",
	},
	{
		to: ROUTES.treasury,
		label: "Treasury",
		desc: "Surplus sleeves, flows, and protocol treasury overview.",
		icon: "treasury",
	},
	{
		to: ROUTES.governance,
		label: "Governance",
		desc: "Buy MMK only-up, stake, vote, and create proposals.",
		icon: "governance",
	},
	{
		to: ROUTES.wallet,
		label: "Wallet",
		desc: "ICRC balances, receive principal, and transfer tokens.",
		icon: "wallet",
	},
	{
		to: ROUTES.savings,
		label: "Savings",
		desc: "USDM DSR-lite — earn from protocol surplus.",
		icon: "savings",
	},
	{
		to: ROUTES.pegswap,
		label: "PegSwap",
		desc: "On-protocol USDM ↔ ckUSDC stability desk.",
		icon: "pegswap",
	},
	{
		to: ROUTES.mmkBook,
		label: "MMK Book",
		desc: "Secondary MMK/USDM limit book (after only-up activation).",
		icon: "liquidity",
	},
	{
		to: ROUTES.liquidity,
		label: "Liquidity",
		desc: "On-chain treasury inventory; bots connect via Agent Layer API keys.",
		icon: "liquidity",
	},
	{
		to: ROUTES.oracle,
		label: "Oracle Layer On-chain",
		desc: "Live collateral price feeds and oracle methodology.",
		icon: "oracle",
	},
	{
		to: ROUTES.cycles,
		label: "Cycle Gas Station",
		desc: "Canister cycles health and gas status.",
		icon: "cycles",
	},
	{
		to: ROUTES.profile,
		label: "Profile",
		desc: "Internet Identity session and account details.",
		icon: "profile",
	},
	{
		to: ROUTES.docs,
		label: "Docs",
		desc: "Protocol documentation — separate docs layer.",
		icon: "docs",
	},
	{
		to: ROUTES.agents,
		label: "Agent Layer",
		desc: "Autonomous keepers — Fast and API agent paths. Skills & Intelligence live here.",
		icon: "agents",
	},
	{
		to: ROUTES.exploreUsdm,
		label: "USDM Explorer",
		desc: "In-app USDM ledger explorer — supply, fee, balance lookup, recent txs.",
		icon: "explore",
	},
	{
		to: ROUTES.exploreMmk,
		label: "MMK Explorer",
		desc: "In-app MMK ledger explorer — supply, fee, balance lookup, recent txs.",
		icon: "explore",
	},
	{
		to: ROUTES.institutions,
		label: "Institutions",
		desc: "Institutional overview and integrations.",
		icon: "institutions",
	},
] as const;

/**
 * Mobile top primary chips (≤4) — not the full product set.
 * Remaining destinations open via More sheet / BottomTab More / More page.
 */
export const MOBILE_PRIMARY_NAV: readonly PrimaryTab[] = [
	{ to: ROUTES.borrow, label: "Borrow" },
	{ to: ROUTES.vaults, label: "Vaults" },
	{ to: ROUTES.liquidation, label: "Liquidate" },
] as const;

/** Mobile bottom: primary actions + More → /more */
export const MOBILE_TABS = [
	{ to: ROUTES.borrow, label: "Borrow", match: "prefix" as const },
	{ to: ROUTES.vaults, label: "Vaults", match: "prefix" as const },
	{ to: ROUTES.liquidation, label: "Liquidate", match: "prefix" as const },
	{ to: ROUTES.more, label: "More", match: "exact" as const },
] as const;

export const DEFI_TABS = PRIMARY_TABS;

/** @deprecated use ALL_SECTIONS — kept for command palette extras */
export const MORE_LINKS = ALL_SECTIONS.map(({ to, label }) => ({ to, label }));

/**
 * More sheet on mobile top nav — full catalog with descriptions.
 */
export const MORE_SHEET_LINKS = ALL_SECTIONS;

/**
 * ⌘K / command palette destinations.
 * Skills + Intelligence are Agent Layer–only deep links (not MORE tiles).
 */
export const COMMAND_LINKS = [
	...ALL_SECTIONS.map(({ to, label }) => ({ to, label })),
	{ to: ROUTES.skills, label: "Skills" },
	{ to: ROUTES.intelligence, label: "Intelligence" },
];

export const MARKETING_LINKS = [
	{ to: ROUTES.motokoDollar, label: "MotokoDollar" },
] as const;

export const MOTOKO_DOLLAR_LOGO = "/assets/motoko-dollar-logo.png";
export const MOTOKO_MAKER_LOGO = MOTOKO_DOLLAR_LOGO;

export const PLATFORM_TAGLINE =
	"ICP-native overcollateralized USD stablecoin";
export const PLATFORM_NAME = "MotokoDollar";

export const AGENT_LAYER_SLOGAN =
	"Autonomous layer for vault liquidations.";
